import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Calculator, Package, Scale, Ruler, Info, ArrowRight, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const ActualWeightVsVolumetricWeightPage = () => {
  const [unit, setUnit] = useState('metric'); // 'metric' or 'imperial'
  const [dimensions, setDimensions] = useState({
    length: '',
    width: '',
    height: '',
    actualWeight: ''
  });
  const [results, setResults] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setDimensions(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const calculateWeight = () => {
    const { length, width, height, actualWeight } = dimensions;
    
    if (!length || !width || !height || !actualWeight) {
      alert('Please fill in all fields');
      return;
    }

    const l = parseFloat(length);
    const w = parseFloat(width);
    const h = parseFloat(height);
    const actual = parseFloat(actualWeight);

    let volumetricWeight;
    let divisor;

    if (unit === 'metric') {
      divisor = 5000; // Standard international divisor for cm³ to kg
      volumetricWeight = (l * w * h) / divisor;
    } else {
      divisor = 139; // Standard for inches³ to lbs
      volumetricWeight = (l * w * h) / divisor;
    }

    const chargeableWeight = Math.max(actual, volumetricWeight);

    setResults({
      actualWeight: actual.toFixed(2),
      volumetricWeight: volumetricWeight.toFixed(2),
      chargeableWeight: chargeableWeight.toFixed(2),
      unit: unit === 'metric' ? 'kg' : 'lb',
      dimensionUnit: unit === 'metric' ? 'cm' : 'in',
      divisor: divisor
    });
  };

  const clearCalculator = () => {
    setDimensions({
      length: '',
      width: '',
      height: '',
      actualWeight: ''
    });
    setResults(null);
  };

  const faqs = [
    {
      question: "What is volumetric weight?",
      answer: "Volumetric weight (also called dimensional weight) is a pricing method used by carriers to account for package size. It's calculated by multiplying the package's length, width, and height, then dividing by a standard divisor. This ensures that large, lightweight packages are charged fairly compared to small, heavy ones."
    },
    {
      question: "How do carriers determine which weight to charge?",
      answer: "Carriers always charge based on the HIGHER of the two weights: actual weight or volumetric weight. This is called the 'chargeable weight.' For example, if your package weighs 5 kg but has a volumetric weight of 8 kg, you'll be charged for 8 kg."
    },
    {
      question: "What divisor should I use?",
      answer: "The standard international divisor is 5000 for metric (cm³ to kg) and 139 for imperial (in³ to lbs). However, some carriers use alternative divisors like 6000 (metric) or 166 (imperial). Always check with your specific carrier for their exact calculation method."
    },
    {
      question: "How can I reduce volumetric weight charges?",
      answer: "To minimize volumetric weight charges: (1) Use the smallest possible box that safely fits your items, (2) Remove excess packaging materials, (3) Consider breaking large shipments into multiple smaller packages, (4) Use custom-sized boxes instead of standard oversized ones."
    },
    {
      question: "Does Shippzy calculate volumetric weight for me?",
      answer: "Yes! When you book with Shippzy, we measure your package dimensions and calculate both actual and volumetric weight automatically. We'll always show you the chargeable weight upfront so there are no surprises. Our team can also advise on optimal packaging to reduce costs."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Actual Weight vs Volumetric Weight Calculator | Shippzy</title>
        <meta name="description" content="Understand the difference between actual weight and volumetric weight for international shipping. Use our free calculator to determine chargeable weight for your packages from Canada." />
        <meta name="keywords" content="volumetric weight calculator, dimensional weight, actual weight vs volumetric weight, shipping weight calculator, chargeable weight, DHL volumetric weight, FedEx dimensional weight" />
        <link rel="canonical" href="https://shippzy.ca/actual-weight-vs-volumetric-weight" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            "name": "How to Calculate Volumetric Weight for International Shipping",
            "description": "Learn how to calculate volumetric weight and understand how carriers determine chargeable weight for parcels.",
            "step": [
              {
                "@type": "HowToStep",
                "name": "Measure Package Dimensions",
                "text": "Measure the length, width, and height of your package in centimeters (or inches)."
              },
              {
                "@type": "HowToStep",
                "name": "Multiply Dimensions",
                "text": "Multiply Length × Width × Height to get the total cubic volume."
              },
              {
                "@type": "HowToStep",
                "name": "Divide by Carrier Divisor",
                "text": "Divide the cubic volume by the carrier's divisor (5000 for metric, 139 for imperial)."
              },
              {
                "@type": "HowToStep",
                "name": "Compare with Actual Weight",
                "text": "Compare the volumetric weight with the actual weight. The higher value is the chargeable weight."
              }
            ]
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-red-600 to-orange-500 text-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
                Actual Weight vs Volumetric Weight
              </h1>
              <p className="text-lg sm:text-xl mb-8 text-white/90">
                Understand how carriers calculate shipping charges and use our free calculator to determine your package's chargeable weight.
              </p>
              <Button 
                className="bg-white text-red-600 hover:bg-gray-100"
                onClick={() => document.getElementById('calculator').scrollIntoView({ behavior: 'smooth' })}
              >
                <Calculator className="mr-2 h-5 w-5" />
                Try Calculator
              </Button>
            </div>
          </div>
        </section>

        {/* Educational Content */}
        <section className="py-16">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="grid md:grid-cols-2 gap-8 mb-16">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center text-2xl">
                    <Scale className="mr-3 h-6 w-6 text-red-600" />
                    Actual Weight
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 mb-4">
                    <strong>Actual weight</strong> is the physical weight of your package measured on a scale. This is straightforward: place your packaged item on a scale and record the weight in kilograms (kg) or pounds (lbs).
                  </p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="font-medium text-gray-900">Example:</p>
                    <p className="text-gray-700">A box containing clothing weighs <strong>3 kg</strong> on the scale.</p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center text-2xl">
                    <Ruler className="mr-3 h-6 w-6 text-orange-600" />
                    Volumetric Weight
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 mb-4">
                    <strong>Volumetric weight</strong> (also called dimensional weight) is a calculated weight based on the package's size. Carriers use this to ensure large, lightweight items are charged fairly, since they occupy valuable cargo space.
                  </p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="font-medium text-gray-900">Formula (Metric):</p>
                    <p className="text-gray-700 font-mono">(L × W × H in cm) ÷ 5000 = kg</p>
                    <p className="font-medium text-gray-900 mt-2">Formula (Imperial):</p>
                    <p className="text-gray-700 font-mono">(L × W × H in inches) ÷ 139 = lbs</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Visual Guide */}
            <div className="bg-gradient-to-r from-red-50 to-orange-50 p-8 rounded-xl mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
                How to Measure Your Package
              </h2>
              <div className="max-w-2xl mx-auto">
                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <div className="flex items-center justify-center mb-6">
                    <Package className="h-24 w-24 text-red-600" />
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <div className="bg-red-600 text-white rounded-full h-8 w-8 flex items-center justify-center flex-shrink-0 mt-1">1</div>
                      <div className="ml-4">
                        <h3 className="font-bold text-gray-900">Length (L)</h3>
                        <p className="text-gray-700">Measure the longest side of your package</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="bg-orange-600 text-white rounded-full h-8 w-8 flex items-center justify-center flex-shrink-0 mt-1">2</div>
                      <div className="ml-4">
                        <h3 className="font-bold text-gray-900">Width (W)</h3>
                        <p className="text-gray-700">Measure the shorter side (perpendicular to length)</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="bg-yellow-600 text-white rounded-full h-8 w-8 flex items-center justify-center flex-shrink-0 mt-1">3</div>
                      <div className="ml-4">
                        <h3 className="font-bold text-gray-900">Height (H)</h3>
                        <p className="text-gray-700">Measure from the base to the top of the package</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 p-4 bg-blue-50 border-l-4 border-blue-600 rounded">
                    <div className="flex items-start">
                      <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <p className="ml-3 text-sm text-blue-900">
                        <strong>Pro Tip:</strong> Always measure the outermost points of your package, including any protruding parts. Round up to the nearest centimeter or inch.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Calculator Section */}
            <div id="calculator" className="bg-white border-2 border-red-600 rounded-xl p-8 mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
                <Calculator className="inline mr-3 h-8 w-8 text-red-600" />
                Interactive Weight Calculator
              </h2>
              
              <div className="max-w-2xl mx-auto">
                {/* Unit Toggle */}
                <div className="flex justify-center gap-4 mb-8">
                  <Button
                    variant={unit === 'metric' ? 'default' : 'outline'}
                    onClick={() => setUnit('metric')}
                    className={unit === 'metric' ? 'bg-red-600' : ''}
                  >
                    Metric (cm / kg)
                  </Button>
                  <Button
                    variant={unit === 'imperial' ? 'default' : 'outline'}
                    onClick={() => setUnit('imperial')}
                    className={unit === 'imperial' ? 'bg-red-600' : ''}
                  >
                    Imperial (in / lbs)
                  </Button>
                </div>

                {/* Input Fields */}
                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Length ({unit === 'metric' ? 'cm' : 'in'})
                    </label>
                    <Input
                      type="number"
                      name="length"
                      value={dimensions.length}
                      onChange={handleInputChange}
                      placeholder="Enter length"
                      step="0.01"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Width ({unit === 'metric' ? 'cm' : 'in'})
                    </label>
                    <Input
                      type="number"
                      name="width"
                      value={dimensions.width}
                      onChange={handleInputChange}
                      placeholder="Enter width"
                      step="0.01"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Height ({unit === 'metric' ? 'cm' : 'in'})
                    </label>
                    <Input
                      type="number"
                      name="height"
                      value={dimensions.height}
                      onChange={handleInputChange}
                      placeholder="Enter height"
                      step="0.01"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Actual Weight ({unit === 'metric' ? 'kg' : 'lbs'})
                    </label>
                    <Input
                      type="number"
                      name="actualWeight"
                      value={dimensions.actualWeight}
                      onChange={handleInputChange}
                      placeholder="Enter weight"
                      step="0.01"
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4 justify-center mb-8">
                  <Button 
                    onClick={calculateWeight}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Calculate
                  </Button>
                  <Button 
                    onClick={clearCalculator}
                    variant="outline"
                  >
                    Clear
                  </Button>
                </div>

                {/* Results */}
                {results && (
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-600 rounded-lg p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                      <CheckCircle className="mr-2 h-6 w-6 text-green-600" />
                      Calculation Results
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700 font-medium">Actual Weight:</span>
                        <span className="text-xl font-bold text-gray-900">{results.actualWeight} {results.unit}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700 font-medium">Volumetric Weight:</span>
                        <span className="text-xl font-bold text-gray-900">{results.volumetricWeight} {results.unit}</span>
                      </div>
                      <div className="h-px bg-gray-300 my-2"></div>
                      <div className="flex justify-between items-center bg-white p-3 rounded">
                        <span className="text-gray-900 font-bold">Chargeable Weight:</span>
                        <span className="text-2xl font-bold text-red-600">{results.chargeableWeight} {results.unit}</span>
                      </div>
                      <p className="text-sm text-gray-600 mt-4">
                        <Info className="inline h-4 w-4 mr-1" />
                        Calculated using divisor: {results.divisor}. Carriers charge based on the higher of actual or volumetric weight.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Key Takeaways */}
            <div className="bg-gray-50 p-8 rounded-xl mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Key Takeaways</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                  <div className="ml-3">
                    <h3 className="font-bold text-gray-900 mb-1">Chargeable Weight Rules</h3>
                    <p className="text-gray-700">You're always charged for whichever is HIGHER: actual weight or volumetric weight.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                  <div className="ml-3">
                    <h3 className="font-bold text-gray-900 mb-1">Carrier Variations</h3>
                    <p className="text-gray-700">Different carriers may use different divisors. Standard is 5000 (metric) or 139 (imperial).</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                  <div className="ml-3">
                    <h3 className="font-bold text-gray-900 mb-1">Minimize Costs</h3>
                    <p className="text-gray-700">Use the smallest box possible and remove excess packaging to reduce volumetric weight charges.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                  <div className="ml-3">
                    <h3 className="font-bold text-gray-900 mb-1">Shippzy Calculates For You</h3>
                    <p className="text-gray-700">When you book with Shippzy, we handle all weight calculations automatically and show you the final cost upfront.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* FAQ Section */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Frequently Asked Questions</h2>
              <div className="space-y-4 max-w-3xl mx-auto">
                {faqs.map((faq, index) => (
                  <Card key={index}>
                    <CardHeader>
                      <CardTitle className="text-lg">{faq.question}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-700">{faq.answer}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* CTA Section */}
            <div className="bg-gradient-to-r from-red-600 to-orange-500 text-white p-8 rounded-xl text-center">
              <h2 className="text-3xl font-bold mb-4">Ready to Ship with Confidence?</h2>
              <p className="text-lg mb-6 text-white/90">
                Let Shippzy handle the complex calculations. Get an instant quote and compare rates from multiple carriers.
              </p>
              <Button 
                className="bg-white text-red-600 hover:bg-gray-100"
                onClick={() => window.location.href = '/get-quote'}
              >
                Get Free Quote
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ActualWeightVsVolumetricWeightPage;
